import { useEffect, useState } from 'react';
import { Outlet, useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { AppBar, Avatar, Toolbar, Typography, Box, useMediaQuery, useTheme, Divider, Menu, MenuItem, styled, IconButton, ToggleButton, ToggleButtonGroup } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useAuth } from "../hooks/useAuth";
import { Icon } from '../components/icons';
import { useColorScheme } from '@mui/material/styles';

// 사이드바 컴포넌트
import SafeSidebar from '../components/SideBar/SafeSidebar';

const MenuItemStyled = styled(MenuItem)(({ theme }) => ({
  '&:hover .MuiBox-root, &:hover .MuiBox-root svg': {
    color: theme.palette.primary.main
  }
}));

const MenuStyles = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  color: 'text.primary',
  textDecoration: 'none',
  '& svg': {
    mr: 2.5,
    fontSize: '1.5rem',
    color: 'text.secondary'
  }
};

const SAFE_APP_MODE_STORAGE_KEY = "safe-app-view-mode";
const SAFE_APP_MODES = {
  ADMIN: "admin",
  WORKER: "worker",
};
const WORKER_MODE_ALLOWED_PATHS = new Set([
  "",
  "monitoring/tbm",
  "monitoring/inspection",
  "monitoring/education",
  "worker/hazard-proposal",
  "worker/action-inbox",
]);

const SafeAppLayout = () => {
  const user = useSelector((state) => state.auth.user);
  const theme = useTheme();
  const auth = useAuth();
  const navigate = useNavigate();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const location = useLocation();
  const { group_code, project_code } = useParams();

  // 아바타 드롭다운
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [safeAppMode, setSafeAppMode] = useState(() => {
    const savedMode = localStorage.getItem(SAFE_APP_MODE_STORAGE_KEY);
    return savedMode === SAFE_APP_MODES.WORKER
      ? SAFE_APP_MODES.WORKER
      : SAFE_APP_MODES.ADMIN;
  });
  const isWorkerMode = safeAppMode === SAFE_APP_MODES.WORKER;
  const isMobileSidebar = isWorkerMode ? !isLgUp : !isMdUp;

  const basePath = `/safe/${group_code}/${project_code}`;
  const currentSafePath =
    location.pathname === basePath
      ? ""
      : location.pathname.startsWith(`${basePath}/`)
      ? location.pathname.slice(basePath.length + 1)
      : "";

  useEffect(() => {
    if (!isMobileSidebar) {
      setMobileSidebarOpen(false);
    }
  }, [isMobileSidebar]);

  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (
      safeAppMode === SAFE_APP_MODES.WORKER &&
      !WORKER_MODE_ALLOWED_PATHS.has(currentSafePath)
    ) {
      navigate(basePath, { replace: true });
    }
  }, [basePath, currentSafePath, navigate, safeAppMode]);

  const handleDropdownOpen = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleDropdownClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    auth.logout();
  };

  // MUI 컬러 스키마 훅 사용
  const { mode, setMode } = useColorScheme();

  // 테마 모드 전환 핸들러
  const handleThemeChange = () => {
    const newMode = mode === 'light' ? 'dark' : 'light';
    setMode(newMode);
    localStorage.setItem('insightwin-mui-mode', newMode);
  };

  const handleSafeAppModeChange = (_, nextMode) => {
    if (!nextMode) return;
    setSafeAppMode(nextMode);
    localStorage.setItem(SAFE_APP_MODE_STORAGE_KEY, nextMode);
  };

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      {/* 상단 전체를 차지하는 AppBar */}
      <AppBar
        color="default"
        elevation={2}
        position="sticky"
        sx={(theme) => ({
          borderBottom: `1px solid ${theme.palette.divider}`,
          borderRadius: 0,
          boxShadow: "none",
          alignItems: "center",
          color: "text.primary",
          justifyContent: "center",
          transition: "border-bottom 0.2s ease-in-out",
          backgroundColor: "rgba(255, 255, 255, 0.88)",
        })}
      >
        <Toolbar
          disableGutters
          sx={{
            width: "100%",
            minHeight: { xs: "64px", md: "64px" },
            paddingInline: { xs: "12px", md: isMobileSidebar ? "16px" : "30px" },
          }}
        >
          <Box 
            sx={{ 
              display: "flex", 
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%"
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
              {isMobileSidebar && (
                <IconButton
                  onClick={() => setMobileSidebarOpen((open) => !open)}
                  sx={{ mr: 1 }}
                  aria-label="메뉴 열기"
                >
                  <MenuIcon />
                </IconButton>
              )}
              <Typography 
                variant={isMobileSidebar ? "h6" : "h4"}
                sx={{ 
                  fontWeight: 500, 
                  lineHeight: "24px", 
                  opacity: 0.8,
                  marginRight: { xs: 1, md: 2 },
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: { xs: 138, sm: "none" },
                }}>
                안전관리 시스템
              </Typography>
            </Box>

            <Box
              sx={{
                display: { xs: mobileSidebarOpen ? "none" : "flex", md: "flex" },
                alignItems: "center",
                gap: { xs: 0.75, sm: 1.25 },
              }}
            >
              <ToggleButtonGroup
                exclusive
                size="small"
                value={safeAppMode}
                onChange={handleSafeAppModeChange}
                aria-label="안전관리 시스템 모드"
                sx={{
                  p: 0.35,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "10px",
                  bgcolor: "background.paper",
                  flexShrink: 0,
                  "& .MuiToggleButton-root": {
                    minWidth: { xs: 44, sm: 64 },
                    px: { xs: 0.8, sm: 1.2 },
                    py: 0.45,
                    border: 0,
                    borderRadius: "8px !important",
                    color: "text.secondary",
                    fontSize: { xs: 12, sm: 13 },
                    fontWeight: 800,
                    lineHeight: 1.2,
                    "&.Mui-selected": {
                      color: "primary.main",
                      bgcolor: "primary.lightOpacity",
                    },
                    "&.Mui-selected:hover": {
                      bgcolor: "primary.lightOpacity",
                    },
                  },
                }}
              >
                <ToggleButton value={SAFE_APP_MODES.ADMIN}>
                  <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                    관리자
                  </Box>
                  <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
                    관
                  </Box>
                </ToggleButton>
                <ToggleButton value={SAFE_APP_MODES.WORKER}>
                  <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                    근로자
                  </Box>
                  <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
                    근
                  </Box>
                </ToggleButton>
              </ToggleButtonGroup>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", gap: '6px' }}>
                <IconButton onClick={handleThemeChange}>
                  <Icon 
                    name={mode === 'light' ? "sunOutlineIcon" : "moonOutlineIcon"} 
                    style={{ 
                      width: '26px', 
                      height: '26px', 
                      '--stroke-width': '3px' 
                    }} 
                  />
                </IconButton>
                <IconButton>
                  <Icon name="bellOutlineIcon" style={{ width: '26px', height: '26px', '--stroke-width': '3px' }} />
                </IconButton>
                <IconButton onClick={handleDropdownOpen}>
                  <Icon name="userIcon" style={{ width: '26px', height: '26px', '--stroke-width': '3px' }} />
                </IconButton>
              </Box>
            </Box>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleDropdownClose}
              sx={{
                "& .MuiMenu-paper": {
                  width: 230,
                  mt: "12px",
                },
              }}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <Box sx={{ display: "flex", alignItems: "center", px: 5, py: 1 }}>
                <Avatar sx={{ width: 38, height: 38, mr: 4 }}>
                  <Icon name="userIcon" />
                </Avatar>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography variant="h6">{user.user_name}</Typography>
                  <Typography variant="body2" sx={{ color: "text.disabled" }}>
                    {user.group_name}
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ my: 1 }} />
              <MenuItemStyled onClick={handleDropdownClose}>
                <Box sx={MenuStyles}>
                  <Icon name="userEditIcon" style={{ width: "22px", height: "22px" }} />
                  프로필 설정
                </Box>
              </MenuItemStyled>
              <MenuItemStyled onClick={handleLogout}>
                <Box sx={MenuStyles}>
                  <Icon name="logoutIcon" style={{ width: "22px", height: "22px" }} />
                  로그아웃
                </Box>
              </MenuItemStyled>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* 사이드바 + 메인 컨텐츠 영역 */}
      <Box sx={{ flex: 1, display: "flex", minWidth: 0, overflow: "hidden" }}>
        {/* 좌측 사이드바 */}
        <SafeSidebar
          isMobile={isMobileSidebar}
          appMode={safeAppMode}
          toggled={mobileSidebarOpen}
          onToggle={setMobileSidebarOpen}
        />

        {/* 컨텐츠 영역 */}
        <Box sx={{ 
          flexGrow: 1,
          minWidth: 0,
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
          bgcolor: 'background.default'
        }}>
          <Outlet context={{ safeAppMode }} />
        </Box>
      </Box>
    </Box>
  );
};

export default SafeAppLayout;
